import { BaseSystemModel, Guid, RecursivePartial, SM } from '@models/base';
import { CodeSnippet } from '@models/shared';
import { TaskFlags } from './task-flags';
import {
    TaskActionType,
    TaskActionUnknown,
    TaskActionNote,
    TaskActionAssign,
    TaskActionStatus,
    TaskActionWatchers,
    TaskActionSubj,
    TaskActionText,
    TaskActionDueDate,
    TaskActionPriority,
    TaskActionSpentHours,
    TaskActionCreateChat,
    TaskActionFileAttach,
    TaskActionVideo,
    TaskActionLinkAdd,
    TaskActionLinkRemove,
    TaskActionFileDetach,
    TaskActionTagAdd,
    TaskActionTagRemove,
    TaskActionNoteEdit,
    TaskActionNoteDelete,
    TaskActionApprovers,
    TaskActionApproverState,
    TaskActionApproversReset,
    TaskActionSubtaskAdd,
    TaskActionSubtaskRemove,
    TaskActionArchive,
    TaskActionSetSprint,
    TaskActionChecklistEdit,
    TaskActionChecklistState,
    TaskActionPlannedHours,
    TaskActionIntegrationEvent,
    TaskActionStartDate,
    TaskActionCustomValue,
} from './actions';
import { TaskLink } from './task-link';
import { TaskApprover } from './task-approver';
import { string2Color } from '@models/utils/colors';
import { TaskChecklist } from './task-checklist';
import { TaskTarget } from './task-target';
import { WorkflowState } from '@models/workflow/workflow-state';
import { TaskStatus } from './task-status';

export type TaskActionClassType = {
    [TaskActionType.Assign]: TaskActionAssign,
    [TaskActionType.Note]: TaskActionNote,
    [TaskActionType.Status]: TaskActionStatus,
    [TaskActionType.Watchers]: TaskActionWatchers,
    [TaskActionType.Subj]: TaskActionSubj,
    [TaskActionType.Text]: TaskActionText,
    [TaskActionType.DueDate]: TaskActionDueDate,
    [TaskActionType.Priority]: TaskActionPriority,
    [TaskActionType.SpentHours]: TaskActionSpentHours,
    [TaskActionType.CreateChat]: TaskActionCreateChat,
    [TaskActionType.FileAttach]: TaskActionFileAttach,
    [TaskActionType.NoteEdit]: TaskActionNoteEdit,
    [TaskActionType.NoteDelete]: TaskActionNoteDelete,
    [TaskActionType.Video]: TaskActionVideo,
    [TaskActionType.LinkAdd]: TaskActionLinkAdd,
    [TaskActionType.LinkRemove]: TaskActionLinkRemove,
    [TaskActionType.FileDetach]: TaskActionFileDetach,
    [TaskActionType.TagAdd]: TaskActionTagAdd,
    [TaskActionType.TagRemove]: TaskActionTagRemove,
    [TaskActionType.Approvers]: TaskActionApprovers,
    [TaskActionType.ApproverState]: TaskActionApproverState,
    [TaskActionType.ApproversReset]: TaskActionApproversReset,
    [TaskActionType.SubtaskAdd]: TaskActionSubtaskAdd,
    [TaskActionType.SubtaskRemove]: TaskActionSubtaskRemove,
    [TaskActionType.Archive]: TaskActionArchive,
    [TaskActionType.SetSprint]: TaskActionSetSprint,
    [TaskActionType.ChecklistEdit]: TaskActionChecklistEdit,
    [TaskActionType.ChecklistState]: TaskActionChecklistState,
    [TaskActionType.PlannedHours]: TaskActionPlannedHours,
    [TaskActionType.IntegrationEvent]: TaskActionIntegrationEvent,
    [TaskActionType.StartDate]: TaskActionStartDate,
    [TaskActionType.CustomValue]: TaskActionCustomValue,
}

export const TaskActionClass = {
    [TaskActionType.Assign]: TaskActionAssign,
    [TaskActionType.Note]: TaskActionNote,
    [TaskActionType.Status]: TaskActionStatus,
    [TaskActionType.Watchers]: TaskActionWatchers,
    [TaskActionType.Subj]: TaskActionSubj,
    [TaskActionType.Text]: TaskActionText,
    [TaskActionType.DueDate]: TaskActionDueDate,
    [TaskActionType.Priority]: TaskActionPriority,
    [TaskActionType.SpentHours]: TaskActionSpentHours,
    [TaskActionType.CreateChat]: TaskActionCreateChat,
    [TaskActionType.FileAttach]: TaskActionFileAttach,
    [TaskActionType.NoteEdit]: TaskActionNoteEdit,
    [TaskActionType.NoteDelete]: TaskActionNoteDelete,
    [TaskActionType.Video]: TaskActionVideo,
    [TaskActionType.LinkAdd]: TaskActionLinkAdd,
    [TaskActionType.LinkRemove]: TaskActionLinkRemove,
    [TaskActionType.FileDetach]: TaskActionFileDetach,
    [TaskActionType.TagAdd]: TaskActionTagAdd,
    [TaskActionType.TagRemove]: TaskActionTagRemove,
    [TaskActionType.Approvers]: TaskActionApprovers,
    [TaskActionType.ApproverState]: TaskActionApproverState,
    [TaskActionType.ApproversReset]: TaskActionApproversReset,
    [TaskActionType.SubtaskAdd]: TaskActionSubtaskAdd,
    [TaskActionType.SubtaskRemove]: TaskActionSubtaskRemove,
    [TaskActionType.Archive]: TaskActionArchive,
    [TaskActionType.SetSprint]: TaskActionSetSprint,
    [TaskActionType.ChecklistEdit]: TaskActionChecklistEdit,
    [TaskActionType.ChecklistState]: TaskActionChecklistState,
    [TaskActionType.PlannedHours]: TaskActionPlannedHours,
    [TaskActionType.IntegrationEvent]: TaskActionIntegrationEvent,
    [TaskActionType.StartDate]: TaskActionStartDate,
    [TaskActionType.CustomValue]: TaskActionCustomValue,
}

export const TaskActionErrorText = {
    [TaskActionType.Assign]: 'Не удалось изменить ответственного',
    [TaskActionType.Note]: 'Не удалось добавить комментарий',
    [TaskActionType.Status]: 'Не удалось изменить статус',
    [TaskActionType.Watchers]: 'Не удалось изменить список наблюдателей',
    [TaskActionType.Subj]: 'Не удалось изменить тему',
    [TaskActionType.Text]: 'Не удалось изменить текст задачи',
    [TaskActionType.DueDate]: 'Не удалось изменить срок выполнения',
    [TaskActionType.Priority]: 'Не удалось изменить приоритет',
    [TaskActionType.SpentHours]: 'Не удалось изменить затраченное время',
    [TaskActionType.CreateChat]: 'Не удалось создать чат для обчуждения задачи',
    [TaskActionType.FileAttach]: 'Не удалось прикрепить файл',
    [TaskActionType.NoteEdit]: 'Не удалось изменить текст комментария',
    [TaskActionType.NoteDelete]: 'Не удалось удалить комментарий',
    [TaskActionType.Video]: 'Не удалось начать видеотрансляцию',
    [TaskActionType.LinkAdd]: 'Не удалось добавить связь с задачей',
    [TaskActionType.LinkRemove]: 'Не удалось удалить связь с задачей',
    [TaskActionType.FileDetach]: 'Не удалось убрать прикрепленный файл',
    [TaskActionType.TagAdd]: 'Не удалось добавить тег',
    [TaskActionType.TagRemove]: 'Не удалось удалить тег',
    [TaskActionType.Approvers]: 'Не удалось изменить список согласующих',
    [TaskActionType.ApproverState]: 'Не удалось изменить статус согласования',
    [TaskActionType.ApproversReset]: 'Не удалось сбросить статусы согласований',
    [TaskActionType.SubtaskAdd]: 'Не удалось добавить подзадачу',
    [TaskActionType.SubtaskRemove]: 'Не удалось удалить подзадачу',
    [TaskActionType.Archive]: 'Не удалось перевести задачу в архив',
    [TaskActionType.SetSprint]: 'Не удалось изменить состояние "активна" задачи',
    [TaskActionType.ChecklistEdit]: 'Не удалось изменить чек-лист',
    [TaskActionType.ChecklistState]: 'Не удалось изменить состояние пункта чек-листа',
    [TaskActionType.PlannedHours]: 'Не удалось изменить запланированное время на выполнение задачи',
    [TaskActionType.IntegrationEvent]: 'Не удалось добавить к задачи событие интеграции',
    [TaskActionType.StartDate]: 'Не удалось изменить дату начала выполнения задачи',
    [TaskActionType.CustomValue]: 'Не удалось установить значение пользовательского поля',
}

export type TaskAction = TaskActionNote | TaskActionAssign | TaskActionStatus | TaskActionWatchers | TaskActionSubj
    | TaskActionText | TaskActionDueDate | TaskActionPriority | TaskActionSpentHours | TaskActionCreateChat | TaskActionFileAttach
    | TaskActionNoteEdit | TaskActionNoteDelete | TaskActionFileDetach | TaskActionVideo | TaskActionLinkAdd | TaskActionLinkRemove
    | TaskActionTagAdd | TaskActionTagRemove | TaskActionApprovers | TaskActionApproverState | TaskActionApproversReset
    | TaskActionSubtaskAdd | TaskActionSubtaskRemove | TaskActionArchive | TaskActionSetSprint | TaskActionChecklistEdit | TaskActionChecklistState
    | TaskActionPlannedHours | TaskActionIntegrationEvent | TaskActionStartDate | TaskActionCustomValue;

export const TaskPriorityName = ['Не задан', 'Высокий', 'Средний', 'Низкий'];
export const TaskPriorityTooltipColor = ['#ccc', 'red', 'orange', 'green'];

export enum TaskType {
    Regular     = 0,            // regular task created by user of organization
    Bugreport   = 1,            // bug report task sent using tech support mechanism
    FeatureReq  = 2,            // feature request task sent using tech support mechanism
    Support     = 3,            // technical support task sent using tech support mechanism
    Other       = 4,            // other kind task sent using tech support mechanism
}

export const MAX_TASK_LENGTH = 1000 * 1024;
export const MAX_TASK_ACTION_LENGTH = 1000 * 1024;

export interface ITaskTpl {
    subj?: string;
    text?: { [key: string]: any };
    orgId?: Guid;
    assignee?: Guid;
    assigneeGroup?: Guid;
    parentId?: Guid;
    projectId?: Guid;
    watchers?: Guid[];
    dueDate?: Date;
    startDate?: Date;
    priority?: number;
    files?: Guid[];
    type?: number;
    typeId?: Guid;
    snippets?: CodeSnippet[];
    tags?: Guid[];
    actions?: TaskAction[];
    plannedHours?: number;
}

export type TaskListType = 'inbox' | 'favorites' | 'inprogress' | 'outgoing' | 'closed' | 'sprint' | 'backlog' | 'support' | 'all';

export class Task extends BaseSystemModel {
    _type = BaseSystemModel.Type.Task;

    shortId!: string;
    orgId!: Guid;
    chatId?: Guid;
    workflowId?: Guid;
    stateId?: Guid;
    status: TaskStatus = TaskStatus.Unknown;
    state?: WorkflowState;
    subj!: string;
    text?: { [key: string]: any };
    assignee?: Guid;
    assigneeGroup?: Guid;
    parentId?: Guid;
    parentTask?: Task;
    projectId?: Guid;
    watchers: Guid[] = [];
    files?: Guid[] = [];
    dueDate?: Date;
    startDate?: Date;
    priority: number = 0;
    spentHours: number = 0;
    chats?: Guid[];
    lastRead?: number;
    pluginId?: Guid;
    snippets?: CodeSnippet[];
    type: number = 0;
    typeId: Guid = '';
    flags: TaskFlags = new TaskFlags();
    tags?: Guid[];
    links?: TaskLink[];
    approvers?: TaskApprover[];
    subTasks?: Task[];
    sprintId?: Guid;
    archived?: boolean;
    checklist?: TaskChecklist;
    plannedHours?: number;
    targets?: TaskTarget[];
    stateStartTime?: Date;
    customValues?: SM<any>;

    _targetsMap?: SM<TaskTarget>;
    _color: string = '#f00';
    _doneSubtasks?: number;
    _done?: boolean;

    constructor(json?: RecursivePartial<Task>) {
        super(json);
        this.parseTask(json || {});
    }

    parse(json: RecursivePartial<Task>): Task {
        super.parse(json);
        this.parseTask(json);
        return this;
    }

    private parseTask(json: RecursivePartial<Task>): void {
        Task.assignFields<Task>(this, json, [
            'shortId',
            'orgId',
            'chatId',
            'workflowId',
            'stateId',
            'status',
            'text',
            'subj',
            'assignee',
            'assigneeGroup',
            'parentId',
            'projectId',
            'priority',
            'spentHours',
            'lastRead',
            'pluginId',
            'type',
            'typeId',
            'sprintId',
            'archived',
            'plannedHours',
            'customValues',
        ]);
        Task.assignArrays<Task>(this, json, ['chats', 'files', 'tags', 'watchers']);
        Task.assignClassArrays<Task>(this, json, { snippets: CodeSnippet, links: TaskLink, approvers: TaskApprover, subTasks: Task, targets: TaskTarget });
        Task.assignClassFields<Task>(this, json, { flags: TaskFlags, checklist: TaskChecklist, state: WorkflowState, parentTask: Task });
        Task.assignDates<Task>(this, json, ['dueDate', 'startDate', 'stateStartTime']);
        if (this.priority > 3) {
            this.priority = 3;
        }
        else if (this.priority < 0) {
            this.priority = 0;
        }
        this._color = this.projectId ? string2Color(this.projectId) : '#f00';
        if (this.targets?.length) {
            this._targetsMap = this.targets.reduce((obj, t) => ({ ...obj, [t.id!]: t }), {});
        }
        else {
            this._targetsMap = undefined;
        }
        if (this.status >= TaskStatus.Done) {
            this._done = true;
        }
        if (this.subTasks?.length) {
            this._doneSubtasks = this.subTasks.filter(t => t._done).length;
        }
    }

}

export function createTaskAction<T extends TaskActionType>(action: TaskAction): TaskActionClassType[T] {
    if (TaskActionClass[action.type]) {
        return new TaskActionClass[action.type](action as any) as TaskActionClassType[T];
    }
    else {
        console.log(`[createTaskAction] unknown action type(${action?.type}):`, TaskActionClass[action.type], JSON.stringify(action), '\n\t ', TaskActionClass, '\n\t ', TaskActionErrorText);
        return new TaskActionUnknown(action) as TaskActionClassType[T];
    }
}

export function isTaskInList(task: Task, list: TaskListType, uid?: Guid, startStateId?: Guid, finishStateId?: Guid): boolean {
    if (list == 'backlog') {
        return !task.sprintId;
    }
    if (list == 'closed') {
        return !!task.archived && !!uid && task.assignee == uid;
    }
    if (list == 'favorites') {
        return !!task.flags?.favorite;
    }
    if (list == 'inbox') {
        return !!task.flags?.inbox;
    }
    if (list == 'inprogress') {
        return task.stateId != startStateId && task.stateId != finishStateId && !!uid && task.assignee == uid;
    }
    if (list == 'outgoing') {
        return !task.archived && !!uid && task.createdBy == uid;
    }
    if (list == 'sprint') {
        return !!task.sprintId;
    }
    if (list == 'support') {
        return task.type > 0 && !!uid && task.createdBy == uid;
    }
    return false;
}
